import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  TextField, 
  Button, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormGroup,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { 
  CalendarToday, 
  Schedule, 
  Person, 
  Phone,
  Email,
  LocalHospital,
  CheckCircle,
  Send
} from '@mui/icons-material';

const Appointment: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    
    // Step 2: Appointment Details
    appointmentType: '',
    preferredDate: '',
    preferredTime: '',
    urgency: '',
    symptoms: '',
    
    // Step 3: Medical History
    hasInsurance: false,
    insuranceProvider: '',
    insuranceNumber: '',
    medicalConditions: [] as string[],
    medications: '',
    allergies: '',
    
    // Step 4: Additional Information
    previousDentist: '',
    referralSource: '',
    specialRequests: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const steps = [
    'Información Personal',
    'Detalles de la Cita',
    'Historial Médico',
    'Información Adicional'
  ];

  const appointmentTypes = [
    'Consulta general',
    'Limpieza dental',
    'Ortodoncia',
    'Implantes dentales',
    'Endodoncia',
    'Cirugía oral',
    'Estética dental',
    'Emergencia dental',
    'Segunda opinión',
    'Otro'
  ];

  const timeSlots = [
    '8:00', '8:30', '9:00', '9:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30'
  ];

  const urgencyLevels = [
    'Rutina - Sin urgencia',
    'Leve - En las próximas semanas',
    'Moderado - En la próxima semana',
    'Alto - En los próximos días',
    'Urgente - Lo antes posible'
  ];

  const medicalConditions = [
    'Diabetes',
    'Hipertensión',
    'Problemas cardíacos',
    'Asma',
    'Alergias',
    'Embarazo',
    'Ninguna'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (condition: string) => {
    setFormData(prev => ({
      ...prev,
      medicalConditions: prev.medicalConditions.includes(condition)
        ? prev.medicalConditions.filter(c => c !== condition)
        : [...prev.medicalConditions, condition]
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Appointment form data:', formData);
    
    setSnackbar({
      open: true,
      message: '¡Cita agendada exitosamente! Te enviaremos una confirmación por email.',
      severity: 'success'
    });

    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      appointmentType: '',
      preferredDate: '',
      preferredTime: '',
      urgency: '',
      symptoms: '',
      hasInsurance: false,
      insuranceProvider: '',
      insuranceNumber: '',
      medicalConditions: [],
      medications: '',
      allergies: '',
      previousDentist: '',
      referralSource: '',
      specialRequests: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    });
    
    setActiveStep(0);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  required
                  fullWidth
                  label="Nombre"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  required
                  fullWidth
                  label="Apellido"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  required
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  fullWidth
                  label="Fecha de nacimiento"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <FormControl fullWidth>
                  <InputLabel>Género</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    label="Género"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="masculino">Masculino</MenuItem>
                    <MenuItem value="femenino">Femenino</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                    <MenuItem value="prefiero-no-decir">Prefiero no decir</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
        );

              case 1:
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Tipo de cita</InputLabel>
                  <Select
                    name="appointmentType"
                    value={formData.appointmentType}
                    label="Tipo de cita"
                    onChange={handleSelectChange}
                  >
                    {appointmentTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Fecha preferida"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <FormControl fullWidth>
                    <InputLabel>Hora preferida</InputLabel>
                    <Select
                      name="preferredTime"
                      value={formData.preferredTime}
                      label="Hora preferida"
                      onChange={handleSelectChange}
                    >
                      {timeSlots.map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Nivel de urgencia</InputLabel>
                  <Select
                    name="urgency"
                    value={formData.urgency}
                    label="Nivel de urgencia"
                    onChange={handleSelectChange}
                  >
                    {urgencyLevels.map((level) => (
                      <MenuItem key={level} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Síntomas o motivo de la consulta"
                  name="symptoms"
                  multiline
                  rows={3}
                  value={formData.symptoms}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Describe brevemente tus síntomas o el motivo de tu consulta..."
                />
              </Box>
            </Box>
          );

              case 2:
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasInsurance}
                      onChange={(e) => setFormData(prev => ({ ...prev, hasInsurance: e.target.checked }))}
                    />
                  }
                  label="¿Tienes seguro médico?"
                />
              </Box>
              {formData.hasInsurance && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      label="Proveedor de seguro"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      label="Número de seguro"
                      name="insuranceNumber"
                      value={formData.insuranceNumber}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
              <Box>
                <FormLabel component="legend">Condiciones médicas (marca todas las que apliquen):</FormLabel>
                <FormGroup>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {medicalConditions.map((condition) => (
                      <Box sx={{ flex: '1 1 200px' }} key={condition}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.medicalConditions.includes(condition)}
                              onChange={() => handleCheckboxChange(condition)}
                            />
                          }
                          label={condition}
                        />
                      </Box>
                    ))}
                  </Box>
                </FormGroup>
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Medicamentos actuales"
                  name="medications"
                  multiline
                  rows={2}
                  value={formData.medications}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Lista los medicamentos que estás tomando actualmente..."
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Alergias"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Alergias a medicamentos, alimentos, etc..."
                />
              </Box>
            </Box>
          );

              case 3:
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  label="Dentista anterior (si aplica)"
                  name="previousDentist"
                  value={formData.previousDentist}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>¿Cómo nos conociste?</InputLabel>
                  <Select
                    name="referralSource"
                    value={formData.referralSource}
                    label="¿Cómo nos conociste?"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="internet">Internet/Búsqueda web</MenuItem>
                    <MenuItem value="redes-sociales">Redes sociales</MenuItem>
                    <MenuItem value="recomendacion">Recomendación de amigo/familiar</MenuItem>
                    <MenuItem value="publicidad">Publicidad</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Solicitudes especiales"
                  name="specialRequests"
                  multiline
                  rows={3}
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Cualquier solicitud especial o información adicional..."
                />
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Contacto de emergencia
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="emergencyContact.name"
                    value={formData.emergencyContact.name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyContact: { ...prev.emergencyContact, name: e.target.value }
                    }))}
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Relación"
                    name="emergencyContact.relationship"
                    value={formData.emergencyContact.relationship}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyContact: { ...prev.emergencyContact, relationship: e.target.value }
                    }))}
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="emergencyContact.phone"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyContact: { ...prev.emergencyContact, phone: e.target.value }
                    }))}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #d8ad82 0%, #ffffff 100%)',
          color: '#2C3E50',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Agenda tu Cita
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Completa el formulario y nos pondremos en contacto contigo para confirmar tu cita
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Stepper */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Form */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box component="form" onSubmit={handleSubmit}>
            {renderStepContent(activeStep)}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Anterior
              </Button>
              
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<Send />}
                  >
                    Enviar Solicitud
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    size="large"
                  >
                    Siguiente
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Information Card */}
        <Card sx={{ mt: 4, backgroundColor: 'primary.light', color: 'white' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📋 Información importante sobre tu cita
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Chip 
                icon={<CheckCircle />} 
                label="Consulta gratuita" 
                sx={{ backgroundColor: 'white', color: 'primary.main' }} 
              />
              <Chip 
                icon={<CheckCircle />} 
                label="Confirmación por email" 
                sx={{ backgroundColor: 'white', color: 'primary.main' }} 
              />
              <Chip 
                icon={<CheckCircle />} 
                label="Cancelación gratuita" 
                sx={{ backgroundColor: 'white', color: 'primary.main' }} 
              />
              <Chip 
                icon={<CheckCircle />} 
                label="Recordatorio por SMS" 
                sx={{ backgroundColor: 'white', color: 'primary.main' }} 
              />
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Appointment; 